import { cn } from '@anytag/shared-utils';
import { SelectOption } from '@anytag/shared-types';
import { HoverCardPortal, HoverCardContent } from '@radix-ui/react-hover-card';
import { CheckBox } from '../../CheckBox';
import { TextCutter } from '../../TextCutter';
import { useSelectContext } from '../select-context';
import { getOptionLabel, getOptionValue } from '../utils';

interface SelectSubItemsProps {
  disabled?: boolean;
  selectedValues: string[];
  option: SelectOption<string>;
  onSelect: (optionValue: string) => void;
}

export const SelectSubItems = ({ option, onSelect, selectedValues, disabled }: SelectSubItemsProps) => {
  const {
    setHoveringOption,
    selectProps: { multiple }
  } = useSelectContext();

  return (
    <HoverCardPortal>
      <HoverCardContent
        side="right"
        sideOffset={0}
        collisionPadding={8}
        onMouseOut={() => setHoveringOption(null)}
        onMouseOver={() => setHoveringOption(getOptionValue(option))}
        className={cn(
          'z-1 max-h-60 overflow-auto rounded-md bg-white p-2 shadow-dropdown',
          'max-w-[min(calc(var(--radix-popover-trigger-width)*2),40rem)]'
        )}
      >
        {option.submenu!.map((opt) => {
          const optValue = getOptionValue(opt);
          const isSelected = selectedValues.includes(optValue);
          const isDisabled = disabled && !isSelected;

          return (
            <div
              key={optValue}
              onClick={isDisabled ? undefined : () => onSelect(optValue)}
              className={cn(
                'flex cursor-default items-center gap-2 rounded py-1 pr-3 pl-2 text-sm/6 text-zinc-900 outline-hidden',
                isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-50',
                { 'pointer-events-none text-zinc-300': isDisabled }
              )}
            >
              {multiple ? <CheckBox checked={isSelected} disabled={isDisabled} /> : null}
              <TextCutter text={getOptionLabel(opt)} lines={1} />
            </div>
          );
        })}
      </HoverCardContent>
    </HoverCardPortal>
  );
};
